/**
 * PropertyMap Component
 * 
 * Displays a Mapbox map for property location.
 * Supports viewing single properties or multiple properties on portfolio view.
 * Features: fullscreen toggle, basemap switcher, responsive sizing.
 * 
 * Note: This uses an iframe to embed Mapbox GL JS for cross-platform compatibility.
 * For native mobile, you'd want to use react-native-maps with Mapbox.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { View, StyleSheet, Text, Platform, Pressable, ActivityIndicator, Modal, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { Property, PropertyStatus } from '../../shared/types/property';
import { GeoFeature, NeighborsByTier, GeoJSONGeometry } from '../../shared/types/parcel';
import { getTierColor, getPricePerAcreColor } from './ParcelOverlay';

// Get API URL - same logic as api.ts
const getApiBaseUrl = () => {
  if (process.env.EXPO_PUBLIC_API_URL) return process.env.EXPO_PUBLIC_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return 'https://api.farmerjohn.pacayagroup.com';
  }
  return 'http://localhost:3001';
};

// Available basemap styles
type BasemapStyle = 'outdoors' | 'streets' | 'satellite' | 'satellite-streets' | 'light' | 'dark';

const BASEMAP_OPTIONS: { id: BasemapStyle; label: string; icon: string; style: string }[] = [
  { id: 'outdoors', label: 'Outdoors', icon: 'trail-sign-outline', style: 'mapbox://styles/mapbox/outdoors-v12' },
  { id: 'streets', label: 'Streets', icon: 'car-outline', style: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'satellite', label: 'Satellite', icon: 'earth-outline', style: 'mapbox://styles/mapbox/satellite-v9' },
  { id: 'satellite-streets', label: 'Hybrid', icon: 'globe-outline', style: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'light', label: 'Light', icon: 'sunny-outline', style: 'mapbox://styles/mapbox/light-v11' },
  { id: 'dark', label: 'Dark', icon: 'moon-outline', style: 'mapbox://styles/mapbox/dark-v11' },
];

interface PropertyMapProps {
  // Single property mode
  property?: Property;
  // Multiple properties mode (portfolio view)
  properties?: Property[];
  // Map height (default uses responsive sizing)
  height?: number;
  // Show controls
  showControls?: boolean;
  // Callback when a property marker is clicked
  onPropertyClick?: (property: Property) => void;
  // Whether to show property info popup
  showPopup?: boolean;
  // Custom center (overrides property location)
  center?: [number, number]; // [lng, lat]
  // Custom zoom level
  zoom?: number;
  // Allow fullscreen expansion
  allowExpand?: boolean;
  // Initial basemap style
  initialBasemap?: BasemapStyle;
  // Show basemap switcher
  showBasemapSwitcher?: boolean;
  // Parcel overlay data
  primaryParcel?: GeoFeature | null;
  // Neighbors grouped by tier
  neighborsByTier?: NeighborsByTier[];
  // Show parcel boundaries
  showParcels?: boolean;
  // Callback when a parcel is clicked
  onParcelClick?: (parcel: GeoFeature, tier: number) => void;
  // Currently selected parcel ID (for highlighting)
  selectedParcelId?: number | null;
  // Owner colors map for family/entity grouping visualization
  ownerColors?: Map<string, string>;
  // Selected owner name (highlights all parcels by that owner)
  selectedOwnerName?: string | null;
  // Color mode: 'tier' uses tier colors, 'owner' uses owner colors, 'value' uses $/acre buckets
  parcelColorMode?: 'tier' | 'owner' | 'value';
}

// Status colors for markers
const STATUS_COLORS: Record<PropertyStatus, string> = {
  research: '#6366f1', // Indigo
  under_contract: '#f59e0b', // Amber
  owned: '#10b981', // Emerald
  listed_for_sale: '#ef4444', // Red
  sold: '#6b7280', // Gray
};

export function PropertyMap({
  property,
  properties = [],
  height,
  showControls = true,
  onPropertyClick,
  showPopup = true,
  center,
  zoom,
  allowExpand = true,
  initialBasemap = 'outdoors',
  showBasemapSwitcher = true,
  primaryParcel,
  neighborsByTier = [],
  showParcels = false,
  onParcelClick,
  selectedParcelId,
  ownerColors,
  selectedOwnerName,
  parcelColorMode = 'tier',
}: PropertyMapProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile, isTablet } = useResponsive();
  
  // Mapbox access token - fetched from backend
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  
  // Map state
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentBasemap, setCurrentBasemap] = useState<BasemapStyle>(initialBasemap);
  const [showBasemapMenu, setShowBasemapMenu] = useState(false);
  
  // Responsive height calculation
  const responsiveHeight = useMemo(() => {
    if (height) return height;
    // Default responsive heights
    if (isSmallMobile) return 250;
    if (isMobile) return 300;
    if (isTablet) return 350;
    return 400; // Desktop
  }, [height, isSmallMobile, isMobile, isTablet]);
  
  // Fetch Mapbox token from backend config endpoint
  useEffect(() => {
    const fetchToken = async () => {
      try {
        // Try env var first (works in dev)
        const envToken = process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN;
        if (envToken) {
          setMapboxToken(envToken);
          setTokenLoading(false);
          return;
        }
        
        // Fetch from backend config endpoint
        const response = await fetch(`${getApiBaseUrl()}/api/config/public`);
        if (response.ok) {
          const config = await response.json();
          setMapboxToken(config.mapbox?.accessToken || null);
        }
      } catch (error) {
        console.warn('Failed to fetch Mapbox token:', error);
      } finally {
        setTokenLoading(false);
      }
    };
    
    fetchToken();
  }, []);
  
  // Use styles for theming
  const colors = {
    background: {
      secondary: styles.tokens.color.surface,
      primary: styles.tokens.color.background,
    },
    text: {
      tertiary: styles.tokens.color.onBackground + '60',
      secondary: styles.tokens.color.onBackground + '80',
      primary: styles.tokens.color.onBackground,
    },
    primary: {
      main: styles.tokens.color.primary,
    },
  };
  const spacing = styles.utils.spacing;

  // Get all properties to display
  const allProperties = useMemo(() => {
    if (property) return [property];
    return properties;
  }, [property, properties]);

  // Filter properties with coordinates
  const mappableProperties = useMemo(() => {
    return allProperties.filter(p => p.latitude && p.longitude);
  }, [allProperties]);

  // Calculate map center and zoom
  const mapConfig = useMemo(() => {
    if (center) {
      return { center, zoom: zoom || 14 };
    }

    if (mappableProperties.length === 0) {
      // Default to center of US
      return { center: [-98.5795, 39.8283] as [number, number], zoom: 4 };
    }

    if (mappableProperties.length === 1) {
      const p = mappableProperties[0];
      return {
        center: [p.longitude!, p.latitude!] as [number, number],
        zoom: zoom || 14,
      };
    }

    // Calculate bounding box for multiple properties
    const lngs = mappableProperties.map(p => p.longitude!);
    const lats = mappableProperties.map(p => p.latitude!);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    
    // Center of bounding box
    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;
    
    // Calculate appropriate zoom (rough approximation)
    const lngDiff = maxLng - minLng;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lngDiff, latDiff);
    let calcZoom = 10;
    if (maxDiff < 0.01) calcZoom = 15;
    else if (maxDiff < 0.05) calcZoom = 13;
    else if (maxDiff < 0.1) calcZoom = 12;
    else if (maxDiff < 0.5) calcZoom = 10;
    else if (maxDiff < 1) calcZoom = 9;
    else if (maxDiff < 5) calcZoom = 7;
    else calcZoom = 5;

    return {
      center: [centerLng, centerLat] as [number, number],
      zoom: zoom || calcZoom,
    };
  }, [mappableProperties, center, zoom]);

  // Get current basemap style URL
  const currentBasemapStyle = useMemo(() => {
    return BASEMAP_OPTIONS.find(b => b.id === currentBasemap)?.style || BASEMAP_OPTIONS[0].style;
  }, [currentBasemap]);

  // No mappable properties
  if (mappableProperties.length === 0) {
    return (
      <View style={[componentStyles.container, { height: responsiveHeight, backgroundColor: colors.background.secondary }]}>
        <View style={componentStyles.emptyState}>
          <Ionicons name="map-outline" size={48} color={colors.text.tertiary} />
          <Text style={[componentStyles.emptyText, { color: colors.text.secondary, marginTop: spacing.md }]}>
            No location data available
          </Text>
          <Text style={[componentStyles.emptySubtext, { color: colors.text.tertiary, marginTop: spacing.xs }]}>
            Add coordinates to view on map
          </Text>
        </View>
      </View>
    );
  }

  // Generate markers GeoJSON
  const markersGeoJSON = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: mappableProperties.map(p => ({
        type: 'Feature',
        properties: {
          id: p.id,
          title: p.street_address || p.address || `Property ${p.id}`,
          status: p.status,
          color: STATUS_COLORS[p.status] || STATUS_COLORS.research,
          value: p.property_value,
          acreage: p.acreage,
        },
        geometry: {
          type: 'Point',
          coordinates: [p.longitude, p.latitude],
        },
      })),
    };
  }, [mappableProperties]);

  // Generate parcels GeoJSON for map overlay
  const parcelsGeoJSON = useMemo(() => {
    if (!showParcels) return null;
    
    const features: any[] = [];
    
    // Helper to get color for a parcel
    const getParcelColor = (ownerName: string | null, tier: number, perAcre: number | null = null) => {
      // If in value color mode, use $/acre buckets
      if (parcelColorMode === 'value') {
        return getPricePerAcreColor(perAcre);
      }
      // If in owner color mode and we have owner colors, use them
      if (parcelColorMode === 'owner' && ownerColors && ownerName) {
        const ownerColor = ownerColors.get(ownerName);
        if (ownerColor) {
          return {
            fill: ownerColor,
            stroke: ownerColor,
            opacity: 0.4,
          };
        }
      }
      // Fallback to tier colors
      return getTierColor(tier);
    };
    
    // Helper to check if parcel is highlighted (selected)
    const isHighlighted = (id: number, ownerName: string | null) => {
      if (selectedParcelId === id) return true;
      if (selectedOwnerName && ownerName === selectedOwnerName) return true;
      return false;
    };
    
    // Add primary parcel
    if (primaryParcel?.geometry_geojson) {
      const color = getTierColor(0); // Always use primary color for subject property
      const highlighted = selectedParcelId === primaryParcel.id;
      
      // Calculate $/acre for popup
      const acres = Number(primaryParcel.area_acres) || 0;
      const totalValue = Number(primaryParcel.total_value) || 0;
      const landValue = Number(primaryParcel.land_value) || 0;
      const perAcre = acres > 0 ? Math.round(totalValue / acres) : null;
      const landPerAcre = acres > 0 && landValue > 0 ? Math.round(landValue / acres) : null;
      
      features.push({
        type: 'Feature',
        id: primaryParcel.id,
        properties: {
          id: primaryParcel.id,
          tier: 0,
          owner_name: primaryParcel.owner_name || 'Subject Property',
          site_address: primaryParcel.site_address,
          area_acres: primaryParcel.area_acres,
          total_value: primaryParcel.total_value,
          land_value: primaryParcel.land_value,
          per_acre: perAcre,
          land_per_acre: landPerAcre,
          fill_color: color.fill,
          stroke_color: color.stroke,
          fill_opacity: highlighted ? 0.7 : color.opacity,
          is_selected: highlighted,
        },
        geometry: primaryParcel.geometry_geojson,
      });
    }
    
    // Add neighbor parcels by tier
    for (const tierData of neighborsByTier) {
      for (const neighborWrapper of tierData.neighbors) {
        // neighborWrapper can be NeighborWithDetails, extract actual neighbor data
        const neighbor = (neighborWrapper as any).neighbor || neighborWrapper;
        if (!neighbor.geometry_geojson && !(neighborWrapper as any).geometry_geojson) continue;
        
        const geometry = neighbor.geometry_geojson || (neighborWrapper as any).geometry_geojson;
        const featureId = neighbor.id || (neighborWrapper as any).neighbor_feature_id;
        const ownerName = neighbor.owner_name;
        
        // Calculate $/acre for coloring & popup
        const acres = Number(neighbor.area_acres) || 0;
        const totalValue = Number(neighbor.total_value) || 0;
        const landValue = Number(neighbor.land_value) || 0;
        const perAcre = acres > 0 ? Math.round(totalValue / acres) : null;
        const landPerAcre = acres > 0 && landValue > 0 ? Math.round(landValue / acres) : null;
        
        const color = getParcelColor(ownerName, tierData.tier, perAcre);
        const highlighted = isHighlighted(featureId, ownerName);
        
        // Dim non-selected parcels when an owner is selected
        const dimmed = selectedOwnerName && ownerName !== selectedOwnerName;
        
        features.push({
          type: 'Feature',
          id: featureId,
          properties: {
            id: featureId,
            tier: tierData.tier,
            owner_name: ownerName,
            site_address: neighbor.site_address,
            area_acres: neighbor.area_acres,
            total_value: totalValue,
            land_value: landValue,
            per_acre: perAcre,
            land_per_acre: landPerAcre,
            fill_color: color.fill,
            stroke_color: highlighted ? '#FFFFFF' : color.stroke,
            fill_opacity: dimmed ? 0.15 : highlighted ? 0.7 : color.opacity,
            is_selected: highlighted,
          },
          geometry: geometry,
        });
      }
    }
    
    return {
      type: 'FeatureCollection',
      features,
    };
  }, [showParcels, primaryParcel, neighborsByTier, selectedParcelId, ownerColors, selectedOwnerName, parcelColorMode]);

  // Build the map HTML for iframe
  const mapHtml = useMemo(() => {
    if (!mapboxToken) return '';
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="initial-scale=1,maximum-scale=1,user-scalable=no">
  <link href="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css" rel="stylesheet">
  <script src="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js"></script>
  <style>
    body { margin: 0; padding: 0; }
    #map { position: absolute; top: 0; bottom: 0; width: 100%; }
    .mapboxgl-popup-content {
      padding: 12px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      max-width: 280px;
    }
    .marker {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      cursor: pointer;
      transition: transform 0.15s ease;
    }
    .marker:hover {
      transform: scale(1.15);
    }
    .parcel-popup-title {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
    }
    .parcel-popup-row {
      font-size: 12px;
      color: #666;
      margin-top: 2px;
    }
    .parcel-tier-badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      margin-bottom: 4px;
    }
  </style>
</head>
<body>
  <div id="map"></div>
  <script>
    mapboxgl.accessToken = '${mapboxToken}';
    
    const map = new mapboxgl.Map({
      container: 'map',
      style: '${currentBasemapStyle}',
      center: [${mapConfig.center[0]}, ${mapConfig.center[1]}],
      zoom: ${mapConfig.zoom},
      attributionControl: ${showControls}
    });

    ${showControls ? `
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
    ` : ''}

    map.on('load', function() {
      ${showParcels && parcelsGeoJSON ? `
      // Add parcels source
      const parcelsData = ${JSON.stringify(parcelsGeoJSON)};
      
      map.addSource('parcels', {
        type: 'geojson',
        data: parcelsData
      });

      // Add parcel fill layer
      map.addLayer({
        id: 'parcels-fill',
        type: 'fill',
        source: 'parcels',
        paint: {
          'fill-color': ['get', 'fill_color'],
          'fill-opacity': ['get', 'fill_opacity']
        }
      });

      // Add parcel outline layer
      map.addLayer({
        id: 'parcels-outline',
        type: 'line',
        source: 'parcels',
        paint: {
          'line-color': ['get', 'stroke_color'],
          'line-width': ['case', ['get', 'is_selected'], 3, 2],
          'line-opacity': 0.9
        }
      });

      // Add click handler for parcels
      map.on('click', 'parcels-fill', function(e) {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const props = feature.properties;
          
          // Tier badge colors
          const tierColors = {
            0: { bg: '#dcfce7', text: '#166534', label: 'Subject' },
            1: { bg: '#dbeafe', text: '#1e40af', label: 'Adjacent' },
            2: { bg: '#ede9fe', text: '#6b21a8', label: 'Tier 2' },
            3: { bg: '#fef3c7', text: '#92400e', label: 'Tier 3' },
            4: { bg: '#fce7f3', text: '#be185d', label: 'Tier 4' },
            5: { bg: '#e0f2fe', text: '#075985', label: 'Tier 5' },
            6: { bg: '#fef9c3', text: '#854d0e', label: 'Tier 6' }
          };
          const tierStyle = tierColors[props.tier] || tierColors[3];
          
          const formatCurrency = (val) => {
            if (!val) return 'N/A';
            return '$' + Number(val).toLocaleString();
          };
          
          const formatAcres = (val) => {
            if (!val) return 'N/A';
            return Number(val).toFixed(2) + ' ac';
          };
          
          const formatPerAcre = (val) => {
            if (!val) return '';
            return '$' + Number(val).toLocaleString() + '/ac';
          };
          
          // Build popup content with $/acre
          let popupContent = '<div style="font-family: system-ui, sans-serif;">' +
            '<span class="parcel-tier-badge" style="background:' + tierStyle.bg + ';color:' + tierStyle.text + ';">' + tierStyle.label + '</span>' +
            '<div class="parcel-popup-title">' + (props.owner_name || 'Unknown Owner') + '</div>' +
            (props.site_address ? '<div class="parcel-popup-row">' + props.site_address + '</div>' : '') +
            '<div class="parcel-popup-row"><strong>Acreage:</strong> ' + formatAcres(props.area_acres) + '</div>' +
            '<div class="parcel-popup-row"><strong>Total Value:</strong> ' + formatCurrency(props.total_value);
          
          // Add $/acre if available
          if (props.per_acre) {
            popupContent += ' <span style="color:#666;">(' + formatPerAcre(props.per_acre) + ')</span>';
          }
          popupContent += '</div>';
          
          // Add land value if available and different from total
          if (props.land_value && props.land_value !== props.total_value) {
            popupContent += '<div class="parcel-popup-row"><strong>Land Value:</strong> ' + formatCurrency(props.land_value);
            if (props.land_per_acre) {
              popupContent += ' <span style="color:#666;">(' + formatPerAcre(props.land_per_acre) + ')</span>';
            }
            popupContent += '</div>';
          }
          
          popupContent += '</div>';
          
          new mapboxgl.Popup({ offset: 10 })
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map);
        }
      });

      // Change cursor on parcel hover
      map.on('mouseenter', 'parcels-fill', function() {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'parcels-fill', function() {
        map.getCanvas().style.cursor = '';
      });
      ` : ''}

      // Add property markers
      const geojson = ${JSON.stringify(markersGeoJSON)};
      
      geojson.features.forEach(feature => {
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundColor = feature.properties.color;
        
        ${showPopup ? `
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML('<div style="font-family: system-ui, sans-serif; min-width: 150px;">' +
            '<strong style="font-size: 14px;">' + feature.properties.title + '</strong>' +
            '<div style="font-size: 12px; color: #666; margin-top: 4px; text-transform: capitalize;">' + 
            feature.properties.status.replace('_', ' ') + '</div>' +
            (feature.properties.value ? '<div style="font-size: 12px; margin-top: 4px;">$' + 
            Number(feature.properties.value).toLocaleString() + '</div>' : '') +
            (feature.properties.acreage ? '<div style="font-size: 12px; color: #666;">' + 
            feature.properties.acreage + ' acres</div>' : '') +
            '</div>');
        ` : ''}
        
        new mapboxgl.Marker(el)
          .setLngLat(feature.geometry.coordinates)
          ${showPopup ? '.setPopup(popup)' : ''}
          .addTo(map);
      });
    });
  </script>
</body>
</html>`;
  }, [mapConfig, markersGeoJSON, parcelsGeoJSON, showControls, showPopup, showParcels, mappableProperties, mapboxToken, currentBasemapStyle]);

  // Map content (reused in both normal and expanded views)
  const renderMapContent = (mapHeight: number | '100%', isFullscreen = false) => (
    <View style={[
      componentStyles.mapWrapper, 
      { height: mapHeight as any },
      isFullscreen && { 
        position: 'absolute' as any, 
        top: 56, // Header height
        left: 0, 
        right: 0, 
        bottom: 0,
        height: 'auto',
      }
    ]}>
      <iframe
        srcDoc={mapHtml}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: isFullscreen ? 0 : 8,
        }}
        title="Property Map"
      />
      
      {/* Control overlay buttons */}
      <View style={[
        componentStyles.controlsOverlay,
        isFullscreen && { top: 16 }
      ]}>
        {/* Expand/Contract button */}
        {allowExpand && (
          <Pressable
            style={[componentStyles.controlButton, { backgroundColor: colors.background.primary }]}
            onPress={() => setIsExpanded(!isExpanded)}
            accessibilityLabel={isExpanded ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <Ionicons 
              name={isExpanded ? 'contract-outline' : 'expand-outline'} 
              size={20} 
              color={colors.text.primary} 
            />
          </Pressable>
        )}
        
        {/* Basemap switcher */}
        {showBasemapSwitcher && (
          <Pressable
            style={[componentStyles.controlButton, { backgroundColor: colors.background.primary }]}
            onPress={() => setShowBasemapMenu(!showBasemapMenu)}
            accessibilityLabel="Change map style"
          >
            <Ionicons name="layers-outline" size={20} color={colors.text.primary} />
          </Pressable>
        )}
      </View>
      
      {/* Basemap selection menu */}
      {showBasemapMenu && (
        <View style={[
          componentStyles.basemapMenu, 
          { backgroundColor: colors.background.primary },
          isFullscreen && { top: 58 }
        ]}>
          {BASEMAP_OPTIONS.map((option) => (
            <Pressable
              key={option.id}
              style={[
                componentStyles.basemapOption,
                currentBasemap === option.id && { backgroundColor: colors.primary.main + '20' },
              ]}
              onPress={() => {
                setCurrentBasemap(option.id);
                setShowBasemapMenu(false);
              }}
            >
              <Ionicons 
                name={option.icon as any} 
                size={18} 
                color={currentBasemap === option.id ? colors.primary.main : colors.text.secondary} 
              />
              <Text 
                style={[
                  componentStyles.basemapLabel, 
                  { color: currentBasemap === option.id ? colors.primary.main : colors.text.primary }
                ]}
              >
                {option.label}
              </Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );

  // On web, use iframe
  if (Platform.OS === 'web') {
    // Show loading state while fetching token
    if (tokenLoading) {
      return (
        <View style={[componentStyles.container, { height: responsiveHeight, backgroundColor: colors.background.secondary }]}>
          <View style={componentStyles.emptyState}>
            <ActivityIndicator size="large" color={colors.primary.main} />
            <Text style={[componentStyles.emptyText, { color: colors.text.secondary, marginTop: spacing.md }]}>
              Loading map...
            </Text>
          </View>
        </View>
      );
    }
    
    // Show error if no token
    if (!mapboxToken) {
      return (
        <View style={[componentStyles.container, { height: responsiveHeight, backgroundColor: colors.background.secondary }]}>
          <View style={componentStyles.emptyState}>
            <Ionicons name="map-outline" size={48} color={colors.text.tertiary} />
            <Text style={[componentStyles.emptyText, { color: colors.text.secondary, marginTop: spacing.md }]}>
              Map unavailable
            </Text>
            <Text style={[componentStyles.emptySubtext, { color: colors.text.tertiary, marginTop: spacing.xs }]}>
              Configure MAPBOX_ACCESS_TOKEN to enable maps
            </Text>
          </View>
        </View>
      );
    }
    
    // Expanded fullscreen modal
    if (isExpanded) {
      return (
        <>
          {/* Placeholder in original position */}
          <View style={[componentStyles.container, { height: responsiveHeight, backgroundColor: colors.background.secondary }]}>
            <View style={componentStyles.emptyState}>
              <Ionicons name="expand-outline" size={32} color={colors.text.tertiary} />
              <Text style={[componentStyles.emptySubtext, { color: colors.text.tertiary, marginTop: spacing.sm }]}>
                Map expanded
              </Text>
            </View>
          </View>
          
          {/* Fullscreen overlay - fills entire viewport */}
          <View style={componentStyles.fullscreenOverlay}>
            {/* Header bar */}
            <View style={componentStyles.fullscreenHeader}>
              <Text style={componentStyles.fullscreenTitle} numberOfLines={1}>
                {mappableProperties.length === 1 
                  ? (mappableProperties[0].street_address || 'Property Map')
                  : `${mappableProperties.length} Properties`
                }
              </Text>
              <Pressable
                style={componentStyles.closeButton}
                onPress={() => setIsExpanded(false)}
                accessibilityLabel="Close fullscreen map"
              >
                <Ionicons name="close" size={24} color="#fff" />
              </Pressable>
            </View>
            
            {/* Map fills remaining space below header */}
            {renderMapContent('100%', true)}
          </View>
        </>
      );
    }
    
    // Normal view
    return (
      <View style={[componentStyles.container, { height: responsiveHeight }]}>
        {renderMapContent(responsiveHeight)}
      </View>
    );
  }

  // On native, show placeholder (would need react-native-maps)
  return (
    <View style={[componentStyles.container, { height: responsiveHeight, backgroundColor: colors.background.secondary }]}>
      <View style={componentStyles.emptyState}>
        <Ionicons name="map-outline" size={48} color={colors.text.tertiary} />
        <Text style={[componentStyles.emptyText, { color: colors.text.secondary, marginTop: spacing.md }]}>
          Map available on web
        </Text>
      </View>
    </View>
  );
}

const componentStyles = StyleSheet.create({
  container: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  mapWrapper: {
    position: 'relative',
    width: '100%',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  controlsOverlay: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    gap: 8,
    zIndex: 10,
  },
  controlButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  basemapMenu: {
    position: 'absolute',
    top: 58,
    left: 12,
    borderRadius: 10,
    padding: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 20,
    minWidth: 150,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  basemapOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 6,
    gap: 10,
  },
  basemapLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  fullscreenOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1a1a1a',
    zIndex: 9999,
  },
  fullscreenHeader: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    backgroundColor: 'rgba(0,0,0,0.85)',
    zIndex: 100,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  fullscreenTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    flex: 1,
    marginRight: 16,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
});

export default PropertyMap;
